"use client";

import { AssetTypes, parseFolderId } from "@zapier/utils";
import { useCallback, useState } from "react";
import { MoveModalInner } from "./core/MoveModalInner";
import { MoveModalProps, OnMoveOptions } from "./core/types";
import { SharingRemovalPrompt } from "./SharingRemovalPrompt";
import { useMoveFolderActions } from "./useMoveFolderActions";

export function MoveFolderModal({
  assetIds,
  assetsAreShared = false,
  assetParentId,
  assetTitle,
  isOpen,
  isAssetOwnedByUser = false,
  optimisticUpdateEnabled,
  setIsOpen,
}: MoveModalProps) {
  const [showSharingRemovalPrompt, setShowSharingRemovalPrompt] =
    useState(false);

  const { moveFolderWithToasts, unnestWithToasts } = useMoveFolderActions({
    optimisticUpdateEnabled: !!optimisticUpdateEnabled,
    assetTitle,
  });
  const sourceFolderIds = (assetIds ?? []).map((id) => parseFolderId(id));

  // Handle moving a folder into another folder
  const handleMoveFolder = useCallback(
    ({
      selectedFolderId,
      selectedFolderName,
      selectedFolderRootId,
    }: OnMoveOptions) => {
      if (!assetIds || assetIds?.length === 0 || !selectedFolderId) {
        return;
      }
      const currentFolderId = parseFolderId(assetIds?.[0]);
      moveFolderWithToasts({
        parentId: selectedFolderId,
        currentFolderId,
        rootId: String(selectedFolderRootId),
        selectedFolderId,
        selectedFolderName,
      });
      setIsOpen(false);
    },
    [assetIds, moveFolderWithToasts, setIsOpen],
  );

  // Handle unnesting a folder/moving a folder to the top level
  const handleUnnest = useCallback(() => {
    if (!assetIds || assetIds?.length === 0) {
      return;
    }

    const folderId = parseFolderId(assetIds?.[0]);
    unnestWithToasts({
      folderId,
    });

    setIsOpen(false);
  }, [assetIds, unnestWithToasts, setIsOpen]);

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return;
  }

  if (showSharingRemovalPrompt) {
    return (
      <SharingRemovalPrompt
        assetTitle={assetTitle ?? "folder"}
        onCancel={() => setShowSharingRemovalPrompt(false)}
        onConfirm={handleUnnest}
      />
    );
  }

  return (
    <MoveModalInner
      assetIds={assetIds}
      sourceFolderIds={sourceFolderIds}
      assetTitle={assetTitle}
      assetType={AssetTypes.Folder}
      assetsAreShared={assetsAreShared}
      assetParentId={assetParentId}
      isAssetOwnedByUser={isAssetOwnedByUser}
      onMove={handleMoveFolder}
      onUnnest={() => {
        setShowSharingRemovalPrompt(true);
      }}
      setIsOpen={setIsOpen}
    />
  );
}
